<script setup lang="ts">
import {computed, h, VNode} from "vue";
import {storeToRefs} from "pinia";
import moment from "moment";
import {useI18n} from "vue-i18n";
import {useClipboard} from "@vueuse/core";
import {useMainStore} from "@/stores/mainStore";
import {useUserStore} from "@/lib/stores/userStore";
import OperationStatus from "@/domains/scheduling/components/operations/OperationStatus.vue";
import {
  DATE_FORMATS,
  TIME_DEFAULT_FORMAT,
  type LoadEventRow,
  type ImportIdObj,
} from "@oplit/shared-module";

enum EventEntryType {
  AutoRescheduling = "AutoRescheduling",
  Import = "Import",
  Manual = "Manual",
}

const props = defineProps<{
  event: LoadEventRow;
}>();

const {t} = useI18n();
const {copy} = useClipboard();

const {users, stationsAndMachinesTagsGroupedById, imports} = storeToRefs(
  useMainStore(),
);
const {isOplitAdmin} = storeToRefs(useUserStore());

const eventExtraInfos = computed<LoadEventRow["event_extra_infos"]>(
  () =>
    props.event.event_extra_infos || ({} as LoadEventRow["event_extra_infos"]),
);
const entryType = computed<EventEntryType>(() => {
  const {rescheduling_id, source_import_id} = eventExtraInfos.value;
  if (rescheduling_id) return EventEntryType.AutoRescheduling;
  if (source_import_id) return EventEntryType.Import;
  return EventEntryType.Manual;
});
const renderedEventValue = computed<VNode>(() => {
  let eventValue = props.event.event_value as string;

  if (props.event.event_field === "op_status") {
    return h(OperationStatus, {
      operation: {op_status: eventValue},
      disabled: true,
    });
  }

  if (props.event.event_field === "secteur_id") {
    const [sector] = stationsAndMachinesTagsGroupedById.value[eventValue] || [];
    if (sector) eventValue = sector.name;
  }
  return h("strong", eventValue);
});
const iconName = computed<string>(() => {
  switch (entryType.value) {
    case EventEntryType.AutoRescheduling:
      return "cog-spin";
    case EventEntryType.Import:
      return "file";
    case EventEntryType.Manual:
      return "user";
    default:
      return "alert-triangle";
  }
});
const fullImport = computed<ImportIdObj>(() =>
  imports.value.find(({id}) => id === eventExtraInfos.value.source_import_id),
);
const displayedTrigger = computed<string>(() => {
  switch (entryType.value) {
    case EventEntryType.AutoRescheduling:
      return t("OperationEventRow.trigger_label__auto_rescheduling");
    case EventEntryType.Import:
      return [
        t("OperationEventRow.trigger_label__import"),
        fullImport.value?.data?.length > 0
          ? `(${fullImport.value.data.join(", ")})`
          : "",
      ].join(" ");
    case EventEntryType.Manual:
      return t("OperationEventRow.trigger_label__manual");
    default:
      return t("OperationEventRow.trigger_label__other");
  }
});
const displayedReference = computed<string>(() => {
  switch (entryType.value) {
    case EventEntryType.AutoRescheduling:
      return eventExtraInfos.value.rescheduling_id;
    case EventEntryType.Import:
      return fullImport.value?.name || eventExtraInfos.value.source_import_id;
    default:
      return t("global.not_applicable");
  }
});
const displayedUser = computed<string>(() => {
  const {updated_by} = eventExtraInfos.value;

  const user = users.value.find((user) => user.id === updated_by);
  if (user) return user.name;

  return isOplitAdmin.value ? updated_by : t("global.not_applicable");
});
</script>

<template>
  <div>
    <div class="fd-flex-center gap-4 text-neutral-secondary text-14">
      <OpalIcon :name="iconName" size="20px" />

      <span class="flex-1 fd-flex-center gap-4">
        <strong>
          {{ event.event_field }}
        </strong>

        ⟶

        <Component :is="renderedEventValue" />
      </span>
    </div>

    <div
      class="operation-event-row__details-container text-12 text-neutral-tertiary"
    >
      <template v-if="isOplitAdmin">
        <span>
          {{ t("OperationEventRow.details__event_id") }}
        </span>

        <span
          class="cursor-pointer text-ellipsis max-w-250px"
          @click="() => copy(event.event_id)"
        >
          {{ event.event_id }}
        </span>

        <span>
          {{ t("OperationEventRow.details__simulation_id") }}
        </span>

        <span class="cursor-pointer" @click="() => copy(event.simulation_id)">
          {{ event.simulation_id }}
        </span>
      </template>

      <span>
        {{ t("OperationEventRow.details__trigger") }}
      </span>

      <span>
        {{ displayedTrigger }}
      </span>

      <span>
        {{ t("OperationEventRow.details__reference") }}
      </span>

      <span class="cursor-pointer" @click="() => copy(displayedReference)">
        {{ displayedReference }}
      </span>

      <span>
        {{ t("OperationEventRow.details__user") }}
      </span>

      <span
        class="cursor-pointer"
        @click="() => copy(eventExtraInfos.updated_by)"
      >
        {{ displayedUser }}
      </span>

      <span>
        {{ t("OperationEventRow.details__date") }}
      </span>

      <span class="cursor-pointer" @click="() => copy(event.event_updated_at)">
        {{
          moment(event.event_updated_at).format(
            `${DATE_FORMATS.FR} [${t("Commons.at")}] ${TIME_DEFAULT_FORMAT}`,
          )
        }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.operation-event-row__details-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  margin-top: 4px;

  & > span:nth-child(even) {
    text-align: right;
  }
}
</style>
